import React, { useEffect, useRef, useState } from 'react';
import QuipaLogo from '../assets/img/quipa_logo.png';
import { Loader } from '../ui_components/loader';
import { getEstimatePreviewShared } from '../api/estimates';
import moment from 'moment';
import { Config } from '../utils';
import { resolveImageSrc } from '../utils/image-src';
import { valuteNumberFormatter } from '../helper/numberFormatter';

type EstimatePreviewProps = {
  match?: any;
};

let initialDataStatus = {
  message: '',
  reason: '',
  success: true,
  unavailable: false,
};

const toFiniteNumber = (value: any, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toNonNegativeNumber = (value: any, fallback = 0) =>
  Math.max(0, toFiniteNumber(value, fallback));

// Snap binary float noise before rounding so half-cent boundaries resolve the
// same way as QBO's decimal arithmetic. Must stay in sync with the helpers in
// EstimateBuilderSheet.tsx and formatMoney in infra/aws/lambdas/estimates/shared.js.
const toDecimalCents = (value: number) => Number((toFiniteNumber(value, 0) * 100).toPrecision(12));

const roundCurrencyAmount = (value: number) => Math.round(toDecimalCents(value)) / 100;

const normalizeStringArray = (value: any) =>
  Array.isArray(value)
    ? [...new Set(value.map((entry) => String(entry || '').trim()).filter(Boolean))]
    : [];

const normalizeDiscountType = (value?: string | null) => (value === 'percent' ? 'percent' : 'amount');

const calculateDiscountedTaxableSubtotal = ({
  subtotal,
  taxableSubtotal,
  discount,
  discountAmount,
}: {
  subtotal: number;
  taxableSubtotal: number;
  discount?: any;
  discountAmount: number;
}) => {
  const safeSubtotal = Math.max(0, toFiniteNumber(subtotal, 0));
  const safeTaxableSubtotal = Math.max(0, toFiniteNumber(taxableSubtotal, 0));
  if (safeSubtotal <= 0 || safeTaxableSubtotal <= 0 || !discount) return safeTaxableSubtotal;

  if (normalizeDiscountType(discount?.type) === 'percent') {
    const discountPercent = Math.min(100, Math.abs(toFiniteNumber(discount?.value, 0)));
    return Math.max(0, roundCurrencyAmount(safeTaxableSubtotal * (1 - discountPercent / 100)));
  }

  const absoluteDiscountAmount = Math.min(safeSubtotal, Math.abs(toFiniteNumber(discountAmount, 0)));
  return Math.max(
    0,
    roundCurrencyAmount(safeTaxableSubtotal * (1 - absoluteDiscountAmount / safeSubtotal))
  );
};

const getPreviewRowTaxableAmount = (row: any, taxableLineIdSet: Set<string>) => {
  if (!row?.taxableEligible || !taxableLineIdSet.has(String(row.id))) return 0;

  const explicitTaxableAmount = toFiniteNumber(row?.taxableAmount, Number.NaN);
  if (Number.isFinite(explicitTaxableAmount) && explicitTaxableAmount > 0) {
    return explicitTaxableAmount;
  }

  return toFiniteNumber(row?.amount, 0);
};

const EstimatePreview = (props: EstimatePreviewProps) => {
  const params = props.match.params || {};
  // Used for estimate info retrieval
  const { id } = params;
  const {} = props;

  const roomsHeaderRef = useRef<any>(null);

  const [data, setData] = useState<any>(null);
  const [dataStatus, setDataStatus] = useState<any>(initialDataStatus);
  const [loading, setLoading] = useState<boolean>(true);
  const [roomsHeaderHeight, setRoomsHeaderHeight] = useState<number>(0);

  useEffect(() => {
    const handleRoomsHeaderHeight = () => {
      setRoomsHeaderHeight(roomsHeaderRef?.current?.offsetHeight);
    };

    handleRoomsHeaderHeight();

    return () => {};
  }, [data]);

  const handleLoading = (val: boolean) => {
    setLoading(val);
  };

  const handleFetchData = async () => {
    handleLoading(true);
    if (!id) {
      console.error('Estimate ID is missing.');
      return;
    }

    const res = await getEstimatePreviewShared(id);
    if (res.success) {
      setDataStatus(initialDataStatus);
      setData(res?.estimate || null);
      handleLoading(false);
    } else {
      setDataStatus({
        message: res?.message,
        reason: res?.reason,
        success: res?.success,
        unavailable: res?.unavailable,
      });
      handleLoading(false);
      console.error(`Something went wrong`, res);
    }
  };

  useEffect(() => {
    handleFetchData();
  }, []);

  const estimateDate = data?.estimateBody?.estimateDate
    ? moment(data?.estimateBody?.estimateDate)?.isValid()
      ? moment(data?.estimateBody?.estimateDate).format('MMM DD, YYYY')
      : ''
    : '';
  const expirationDate = data?.estimateBody?.expirationDate
    ? moment(data?.estimateBody?.expirationDate)?.isValid()
      ? moment(data?.estimateBody?.expirationDate).format('MMM DD, YYYY')
      : ''
    : '';

  const rooms = data?.estimateBody?.preview?.roomSections || [];
  const projectFees = data?.estimateBody?.preview?.projectFees || [];
  const projectFeesTotal = projectFees?.reduce(
    (acc: number, value: any) => acc + (value?.amount || 0),
    0
  );
  const taxMode =
    data?.estimateBody?.tax?.mode || data?.estimateBody?.preview?.taxMode || 'excluded';
  const taxPercent = toNonNegativeNumber(
    data?.estimateBody?.tax?.taxPercent ??
      data?.estimateBody?.tax?.clientTaxRate ??
      data?.estimateBody?.preview?.taxPercent ??
      data?.estimateBody?.preview?.clientTaxRate,
    0
  );
  const taxableLineIdSet = new Set(
    normalizeStringArray(
      data?.estimateBody?.tax?.taxableLineIds || data?.estimateBody?.preview?.taxableLineIds || []
    )
  );
  const roomRows = rooms.flatMap((room: any) => (Array.isArray(room?.rows) ? room.rows : []));
  const allTaxableRows = [...roomRows, ...projectFees];
  const taxableSubtotal = allTaxableRows.reduce((sum: number, row: any) => {
    return sum + getPreviewRowTaxableAmount(row, taxableLineIdSet);
  }, 0);
  const subTotal =
    (data?.estimateBody?.preview?.totals?.feesSubtotal || 0) +
    (data?.estimateBody?.preview?.totals?.itemsSubtotal || 0);
  const discount = data?.estimateBody?.preview?.discount || data?.estimateBody?.discount || null;
  const discountAmount = roundCurrencyAmount(
    toFiniteNumber(
      discount?.amount ??
        data?.estimateBody?.preview?.totals?.discountAmount ??
        (discount?.type === 'percent'
          ? -(subTotal * Math.abs(toFiniteNumber(discount?.value, 0))) / 100
          : discount?.value),
      0
    )
  );
  const discountLabel = String(discount?.label || 'Discount').trim() || 'Discount';
  const taxableSubtotalAfterDiscount = calculateDiscountedTaxableSubtotal({
    subtotal: subTotal,
    taxableSubtotal,
    discount,
    discountAmount,
  });

  let billingInfo = data?.project?.billToParent
    ? data?.project?.billingInfo
    : data?.project?.billingInfoDirectly;

  const showTaxName = () => {
    if (taxMode === 'excluded') {
      return 'Tax Excluded';
    } else if (['manual', 'calculated'].includes(taxMode)) {
      return 'Estimated Tax';
    }
    return `Tax ${taxMode}`;
  };

  const calculatedTaxAmount = roundCurrencyAmount(taxableSubtotalAfterDiscount * (taxPercent / 100));
  const manualTaxAmount = toNonNegativeNumber(
    data?.estimateBody?.tax?.manualAmount ??
      data?.estimateBody?.preview?.taxAmount ??
      data?.estimateBody?.preview?.totals?.taxAmount,
    0
  );
  const estimatedTaxAmount =
    taxMode === 'excluded'
      ? 0
      : taxMode === 'manual'
        ? manualTaxAmount
        : taxMode === 'calculated'
          ? calculatedTaxAmount
          : toFiniteNumber(data?.estimateBody?.preview?.totals?.taxAmount, 0);

  const showTaxValue = () => {
    if (taxMode !== 'excluded') {
      return valuteNumberFormatter(estimatedTaxAmount);
    }
    return '—';
  };

  const handleAccountInfo = () => {
    let stringArr: string[] = [];

    if (data?.account?.address) stringArr.push(data?.account?.address);
    if (data?.account?.suite) stringArr.push(data?.account?.suite);

    let addressStringArray: string[] = [];
    if (data?.account?.city) addressStringArray.push(data?.account?.city);
    if (data?.account?.state) addressStringArray.push(data?.account?.state);
    if (data?.account?.zip) addressStringArray.push(data?.account?.zip);
    let addressString = addressStringArray.join(' ');

    if (addressString) {
      stringArr.push(addressString);
    }

    if (stringArr?.length <= 0) return '';
    return stringArr.join('\n');
  };

  const showClientInformations = () => {
    let stringArr: string[] = [];

    if (billingInfo?.company) stringArr.push(billingInfo?.company);
    if (billingInfo?.streetAddress) stringArr.push(billingInfo?.streetAddress);
    if (billingInfo?.unitSuite) stringArr.push(billingInfo?.unitSuite);

    let addressStringArray: string[] = [];
    if (billingInfo?.cityTown) addressStringArray.push(`${billingInfo?.cityTown},`);
    if (billingInfo?.province) addressStringArray.push(billingInfo?.province);
    if (billingInfo?.zipCode) addressStringArray.push(billingInfo?.zipCode);
    let addressString = addressStringArray.join(' ');

    if (addressString) {
      stringArr.push(addressString);
    }

    if (stringArr?.length <= 0) return '';
    return stringArr.filter((f: any) => f).join('\n');
  };

  const showProjectLocationInformations = () => {
    let stringArr: string[] = [];

    let project = data?.project;

    if (project?.company) stringArr.push(project?.company);
    if (project?.shippingStreetAddress) stringArr.push(project?.shippingStreetAddress);
    if (project?.shippingUnitSuite) stringArr.push(project?.shippingUnitSuite);

    let addressStringArray: string[] = [];
    if (project?.shippingCityTown) addressStringArray.push(`${project?.shippingCityTown},`);
    if (project?.shippingStateProvince) addressStringArray.push(project?.shippingStateProvince);
    if (project?.shippingZIPCode) addressStringArray.push(project?.shippingZIPCode);
    let addressString = addressStringArray.join(' ');

    if (addressString) {
      stringArr.push(addressString);
    }

    if (stringArr?.length <= 0) return '';
    return stringArr.filter((f: any) => f).join('\n');
  };

  const clientFullName = () => {
    let stringArr: string[] = [];

    if (billingInfo?.firstName) stringArr.push(billingInfo?.firstName);
    if (billingInfo?.lastName) stringArr.push(billingInfo?.lastName);

    if (stringArr.length <= 0) return '';
    return stringArr.join(' ');
  };

  const projectFullName = () => {
    let stringArr: string[] = [];

    if (data?.project?.firstName) stringArr.push(data?.project?.firstName);
    if (data?.project?.lastName) stringArr.push(data?.project?.lastName);

    if (stringArr.length <= 0) return data?.project?.displayName || '';
    return stringArr.join(' ');
  };

  const estimatedGrandTotal = roundCurrencyAmount(subTotal + discountAmount + estimatedTaxAmount);
  const hasCompanyLogo = Boolean(data?.account?.logoId);

  // RENDER
  if (loading) {
    return <Loader className="fixed top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2" />;
  }

  if (['deleted', 'expired'].includes(dataStatus?.reason)) {
    return (
      <div data-qa="estimate-public-preview" className="min-h-screen bg-white text-gray-900 print:bg-white">
        <div className="mx-auto max-w-[850px] md:px-8 md:py-10 px-5 py-5">
          <div>
            <p className="m-0 text-center text-2xl font-medium">Estimate Not Found</p>
            <p className="m-0 text-center text-gray-500 text-lg">This estimate is not available.</p>
          </div>
        </div>
      </div>
    );
  }

  const clientName = clientFullName();
  const clientInformationLines = showClientInformations()
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const promotedClientInfoLine = clientName ? '' : clientInformationLines[0] || '';
  const clientDetailLines = clientName ? clientInformationLines : clientInformationLines.slice(1);

  return (
    <div data-qa="estimate-public-preview" className="min-h-screen bg-white text-gray-900 print:bg-white">
      <div className="mx-auto max-w-[850px] md:px-8 md:py-10 px-5 py-5">
        {/* Logo and estimate info */}
        <div className="flex flex-row flex-wrap items-start justify-between md:gap-8 gap-[10px] mb-6">
          {hasCompanyLogo && (
            <div className="order-1 shrink-0 min-w-[110px] max-w-[200px] h-[110px] flex items-center">
              <img
                alt="Company logo"
                src={`${Config.fileStoreUrl}${data?.account?.logoId}`}
                className="h-full w-full object-contain"
              />
            </div>
          )}
          <div className="order-2 md:flex-1 min-w-0">
            <div className="text-sm">
              <p className="wrap-text m-0 font-semibold text-gray-900 wrap-text text-base">
                {data?.account?.companyName}
              </p>
              <p className="wrap-text m-0 mt-[4px] text-gray-600 whitespace-pre-line text-sm">
                {handleAccountInfo()}
              </p>
              <p className="wrap-text m-0 mt-[4px] text-gray-600 text-sm">
                {data?.account?.email || ''}
              </p>
            </div>
          </div>
          <div className="order-3 md:flex-1 min-w-0 md:text-right text-sm">
            <p className="m-0 wrap-text font-semibold text-base">Estimate # {data?.baseNumber}</p>
            <p className="m-0 mt-[4px] text-gray-600">Date: {estimateDate}</p>
            <p className="text-gray-600">Expires on: {expirationDate}</p>
          </div>
        </div>
        {/* Client and Project location */}
        <div className="grid grid-cols-2 gap-8 mb-4 text-sm md:px-8 px-4">
          {/* this is the highest parent client info and its billing info */}
          <div>
            <p className="text-teal font-bold text-xs uppercase tracking-wider mb-1">Client</p>
            {clientName ? (
              <p className="wrap-text m-0 font-semibold text-gray-900">{clientName}</p>
            ) : promotedClientInfoLine ? (
              <p className="wrap-text m-0 font-semibold text-gray-900">{promotedClientInfoLine}</p>
            ) : null}
            <p className="wrap-text leading-tight text-gray-600 whitespace-pre-line text-xs mt-0.5">
              {clientDetailLines.join('\n')}
            </p>
          </div>
          <div>
            <p className="text-teal font-bold text-xs uppercase tracking-wider mb-1">
              Project Location
            </p>
            <p className="wrap-text m-0 font-semibold text-gray-900">{projectFullName()}</p>
            <p className="wrap-text leading-tight text-gray-600 whitespace-pre-line text-xs mt-0.5">
              {showProjectLocationInformations()}
            </p>
          </div>
        </div>
        {/* Project Site Mark */}
        {data?.project?.displayName && (
          <div className="mb-4 text-sm">
            <p className="wrap-text text-gray-500 italic">Project: {data?.project?.displayName}</p>
          </div>
        )}

        {/* todo: table */}
        {/* {rooms?.length > 0 && (
          <div className="relative">
            <table>
              <colgroup>
                <col className="md:w-[120px] w-[20%]" />
                <col className="md:w-[auto] w-[50%]" />
                <col className="md:w-[60px] w-[15%]" />
                <col className="md:w-[90px] w-[15%]" />
              </colgroup>
              <thead>
                <tr className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  <th className="py-2 text-left">
                    <div>Item Name</div>
                  </th>
                  <th className="py-2 text-left">
                    <div>Description</div>
                  </th>
                  <th className="py-2 text-right">
                    <div>Qty</div>
                  </th>
                  <th className="py-2 text-right">
                    <div>Amount</div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {rooms?.map((item: any, index: number) => {
                  const groupedByOrder = item?.rows?.reduce((acc: any, item: any) => {
                    const key = item?.orderId;

                    if (!acc[key]) {
                      acc[key] = {
                        parent: null,
                        children: [],
                      };
                    }

                    if (item.rowType === 'item') {
                      acc[key].parent = item;
                    } else {
                      acc[key].children.push(item);
                    }

                    return acc;
                  }, {});

                  return (
                    <Fragment key={index}>
                      {Object.entries(groupedByOrder).map(([key, value]: any) => {
                        const parent = value?.parent || null;
                        const children = value?.children || [];
                        const childrenWo = children?.filter((f: any) => !f.isCom) || [];
                        const childrenCom = children?.filter((f: any) => f.isCom) || [];

                        return (
                          <Fragment key={key}>
                            <tr>
                              <td colSpan={2}>
                                <div className="col-span-2">
                                  <span className="wrap-text text-sm font-semibold text-primary">
                                    {parent?.itemLabel || ''}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div className="text-right text-xs text-gray-600">
                                  {parent?.quantity}
                                </div>
                              </td>
                              <td>
                                {parent?.includedInPrice ? (
                                  <div className="text-right text-xs text-gray-500 italic">
                                    Included
                                  </div>
                                ) : (
                                  <div className="text-right text-sm">
                                    <div>
                                      <span className="text-right text-sm font-semibold">
                                        {valuteNumberFormatter(parent?.amount)}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </td>
                            </tr>

                            {childrenWo?.map((c: any, cIdx: number) => {
                              const isFeeRow =
                                c?.rowType === 'project-fee' || c?.rowType === 'order-fee';
                              const isServiceRow = c?.rowType === 'service';
                              const isFabricationRow = c?.rowType === 'fabrication';

                              return (
                                <tr key={cIdx}>
                                  <td>
                                    <div className="rounded">
                                      {c?.photoId ? (
                                        <div className="h-[56px] w-[56px]">
                                          <img
                                            alt={''}
                                            src={resolveImageSrc(c?.photoId)}
                                            className="h-full w-full rounded object-cover"
                                          />
                                        </div>
                                      ) : null}
                                    </div>
                                  </td>
                                  <td>
                                    <div>
                                      {(c?.qboType || '')?.toLowerCase()?.includes('hardware') && (
                                        <p className="m-0 text-xs text-gray-600 wrap-text whitespace-pre-wrap">
                                          HARDWARE
                                        </p>
                                      )}
                                      {(c?.qboType || '')?.toLowerCase()?.includes('headrail') && (
                                        <p className="m-0 text-xs text-gray-600 wrap-text whitespace-pre-wrap">
                                          HEADRAIL
                                        </p>
                                      )}
                                      {['parts', 'supplies'].some((s) =>
                                        (c?.qboType || '')?.toLowerCase()?.includes(s)
                                      ) && (
                                        <p className="m-0 text-xs text-gray-600 wrap-text whitespace-pre-wrap">
                                          PARTS & SUPPLIES
                                        </p>
                                      )}
                                      {isFeeRow && (
                                        <p className="m-0 text-xs text-sky-700 font-semibold wrap-text whitespace-pre-wrap">
                                          {c?.itemLabel || ''}
                                        </p>
                                      )}
                                      <p className="m-0 text-xs text-gray-600 wrap-text whitespace-pre-wrap">
                                        {c?.descriptionShort || c?.description || ''}
                                      </p>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="text-right text-xs text-gray-600">
                                      {c?.quantity}{' '}
                                      {(c?.rowType === 'component' &&
                                        ['hardware', 'headrail', 'parts', 'supplies'].some((s) =>
                                          (c?.qboType || '')?.toLowerCase()?.includes(s)
                                        )) ||
                                      isFeeRow ||
                                      isServiceRow ||
                                      isFabricationRow
                                        ? ''
                                        : 'yd'}
                                    </div>
                                  </td>
                                  <td>
                                    {c?.includedInPrice ? (
                                      <div className="text-right text-xs text-gray-500 italic">
                                        Included
                                      </div>
                                    ) : (
                                      <div className="text-right text-sm">
                                        <div>
                                          <span className="text-right text-sm font-semibold">
                                            {valuteNumberFormatter(c?.amount)}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            {childrenCom?.length > 0 && (
                              <div className="grid md:grid-cols-[120px_1fr_60px_90px] grid-cols-[1fr_2fr_1fr_1fr] gap-3 px-2 pt-1">
                                <div></div>
                                <div>
                                  <p className="text-teal text-xs font-bold uppercase tracking-wider mb-1">
                                    CLIENT PROVIDED
                                  </p>
                                </div>
                                <div></div>
                                <div></div>
                              </div>
                            )}
                            {childrenCom?.map((c: any, cIdx: number) => {
                              const isFeeRow =
                                c?.rowType === 'project-fee' || c?.rowType === 'order-fee';
                              const isServiceRow = c?.rowType === 'service';
                              const isFabricationRow = c?.rowType === 'fabrication';

                              return (
                                <div
                                  key={cIdx}
                                  className="grid md:grid-cols-[120px_1fr_60px_90px] grid-cols-[1fr_2fr_1fr_1fr] gap-3 py-2 px-2 items-start"
                                >
                                  <div className="rounded">
                                    {c?.photoId ? (
                                      <div className="h-[56px] w-[56px]">
                                        <img
                                          alt={''}
                                          src={resolveImageSrc(c?.photoId)}
                                          className="h-full w-full rounded object-cover"
                                        />
                                      </div>
                                    ) : null}
                                  </div>

                                  <div>
                                    {(c?.qboType || '')?.toLowerCase()?.includes('hardware') && (
                                      <p className="m-0 text-xs text-gray-600 wrap-text whitespace-pre-wrap">
                                        HARDWARE
                                      </p>
                                    )}
                                    {(c?.qboType || '')?.toLowerCase()?.includes('headrail') && (
                                      <p className="m-0 text-xs text-gray-600 wrap-text whitespace-pre-wrap">
                                        HEADRAIL
                                      </p>
                                    )}
                                    {['parts', 'supplies'].some((s) =>
                                      (c?.qboType || '')?.toLowerCase()?.includes(s)
                                    ) && (
                                      <p className="m-0 text-xs text-gray-600 wrap-text whitespace-pre-wrap">
                                        PARTS & SUPPLIES
                                      </p>
                                    )}
                                    {isFeeRow && (
                                      <p className="m-0 text-xs text-sky-700 font-semibold wrap-text whitespace-pre-wrap">
                                        {c?.itemLabel || ''}
                                      </p>
                                    )}
                                    <p className="m-0 text-xs text-gray-600 wrap-text whitespace-pre-wrap">
                                      {c?.descriptionShort || c?.description || ''}
                                    </p>
                                  </div>
                                  <div className="text-right text-xs text-gray-600">
                                    {c?.quantity}{' '}
                                    {(c?.rowType === 'component' &&
                                      ['hardware', 'headrail', 'parts', 'supplies'].some((s) =>
                                        (c?.qboType || '')?.toLowerCase()?.includes(s)
                                      )) ||
                                    isFeeRow ||
                                    isServiceRow ||
                                    isFabricationRow
                                      ? ''
                                      : 'yd'}
                                  </div>
                                  <div className="text-right text-xs text-gray-500 italic">COM</div>
                                </div>
                              );
                            })}
                          </Fragment>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )} */}

        {/* Table header */}
        {rooms?.length > 0 && (
          <div className="relative">
            <div className="sticky top-0 z-[2] bg-white border-b-2 border-teal mb-1">
              <div
                ref={roomsHeaderRef}
                className="grid md:grid-cols-[120px_1fr_60px_90px] grid-cols-[1fr_2fr_1fr_1fr] gap-3 py-2 text-xs font-bold uppercase tracking-wider text-gray-500"
              >
                <div>Item Name</div>
                <div>Description</div>
                <div className="text-right">Qty</div>
                <div className="text-right">Amount</div>
              </div>
            </div>
            {rooms?.map((item: any, index: number) => {
              const groupedByOrder = item?.rows?.reduce((acc: Map<any, any>, item: any) => {
                const key = item?.orderId;

                if (!acc.has(key)) {
                  acc.set(key, {
                    parent: null,
                    children: []
                  })
                }

                const group = acc.get(key)

                if (item.rowType === 'item') {
                  group.parent = item;
                } else {
                  group.children.push(item);
                }

                return acc;
              }, new Map());

              return (
                <div key={index} className="mb-2">
                  <div
                    style={{ top: `${roomsHeaderHeight}px` }}
                    className={`sticky z-[1] bg-[#EAF3F6] grid md:grid-cols-[120px_1fr_60px_90px] grid-cols-[1fr_2fr_1fr_1fr] gap-3 border-b border-gray-200 py-2 px-2`}
                  >
                    <span className="font-bold text-sm col-span-3">{item?.roomName || ''}</span>
                    <span className="font-bold text-sm text-right">
                      {valuteNumberFormatter(item?.subtotal)}
                    </span>
                  </div>

                  <div>
                    {Array.from(groupedByOrder.entries()).map(([key, value]: any) => {
                      const parent = value?.parent || null;
                      const children = value?.children || [];
                      const childrenWo = children?.filter((f: any) => !f.isCom) || [];
                      const childrenCom = children?.filter((f: any) => f.isCom) || [];
                      const parentDescription =
                        String(parent?.descriptionShort || parent?.description || '').trim();
                      const parentLabel = String(parent?.itemLabel || '').trim();
                      const shouldShowParentDescription =
                        parentDescription && parentDescription !== parentLabel;

                      return (
                        <div key={key} className="border-b border-gray-300">
                          <div className="grid md:grid-cols-[120px_1fr_60px_90px] grid-cols-[1fr_2fr_1fr_1fr] gap-3 pt-2 px-2 items-start">
                            <div>
                              <span className="wrap-text text-sm font-semibold text-primary">
                                {parent?.itemLabel || ''}
                              </span>
                            </div>
                            <div>
                              {shouldShowParentDescription ? (
                                <p className="m-0 text-xs text-gray-600 wrap-text whitespace-pre-wrap">
                                  {parentDescription}
                                </p>
                              ) : null}
                            </div>

                            <div className="text-right text-xs text-gray-600">
                              {parent?.quantity}
                            </div>

                            {parent?.includedInPrice ? (
                              <div className="text-right text-xs text-gray-500 italic">
                                Included
                              </div>
                            ) : (
                              <div className="text-right text-sm">
                                <div>
                                  <span className="text-right text-sm font-semibold">
                                    {valuteNumberFormatter(parent?.amount)}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>

                          {childrenWo?.map((c: any, cIdx: number) => {
                            const isFeeRow =
                              c?.rowType === 'project-fee' || c?.rowType === 'order-fee';
                            const isServiceRow = c?.rowType === 'service';
                            const isFabricationRow = c?.rowType === 'fabrication';

                            return (
                              <div
                                key={cIdx}
                                className="grid md:grid-cols-[120px_1fr_60px_90px] grid-cols-[1fr_2fr_1fr_1fr] gap-3 py-2 px-2 items-start"
                              >
                                
                                <div className="rounded">
                                  {c?.photoId ? (
                                    <div className="h-[56px] w-[56px]">
                                      <img
                                        alt={''}
                                        src={resolveImageSrc(c?.photoId)}
                                        className="h-full w-full rounded object-cover"
                                      />
                                    </div>
                                  ) : null}
                                </div>

                                <div>
                                  {(c?.qboType || '')?.toLowerCase()?.includes('hardware') && (
                                    <p className="m-0 text-xs text-gray-600 wrap-text whitespace-pre-wrap">
                                      HARDWARE
                                    </p>
                                  )}
                                  {(c?.qboType || '')?.toLowerCase()?.includes('headrail') && (
                                    <p className="m-0 text-xs text-gray-600 wrap-text whitespace-pre-wrap">
                                      HEADRAIL
                                    </p>
                                  )}
                                  {['parts', 'supplies'].some((s) =>
                                    (c?.qboType || '')?.toLowerCase()?.includes(s)
                                  ) && (
                                    <p className="m-0 text-xs text-gray-600 wrap-text whitespace-pre-wrap">
                                      {String(c?.displayTypeLabel || c?.qboItemName || '').trim() ||
                                        'Parts & Supplies'}
                                    </p>
                                  )}
                                  {isFeeRow && (
                                    <p className="m-0 text-xs text-sky-700 font-semibold wrap-text whitespace-pre-wrap">
                                      {c?.itemLabel || ''}
                                    </p>
                                  )}
                                  <p className="m-0 text-xs text-gray-600 wrap-text whitespace-pre-wrap">
                                    {c?.descriptionShort || c?.description || ''}
                                  </p>
                                </div>
                                <div className="text-right text-xs text-gray-600">
                                  {c?.quantity}{' '}
                                  {(c?.rowType === 'component' &&
                                    ['hardware', 'headrail', 'parts', 'supplies'].some((s) =>
                                      (c?.qboType || '')?.toLowerCase()?.includes(s)
                                    )) ||
                                  isFeeRow ||
                                  isServiceRow ||
                                  isFabricationRow
                                    ? ''
                                    : 'yd'}
                                </div>
                                {c?.includedInPrice ? (
                                  <div className="text-right text-xs text-gray-500 italic">
                                    Included
                                  </div>
                                ) : (
                                  <div className="text-right text-sm">
                                    <div>
                                      <span className="text-right text-sm font-semibold">
                                        {valuteNumberFormatter(c?.amount)}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {childrenCom?.length > 0 && (
                            <div className="grid md:grid-cols-[120px_1fr_60px_90px] grid-cols-[1fr_2fr_1fr_1fr] gap-3 px-2 pt-1">
                              <div></div>
                              <div>
                                <p className="text-teal text-xs font-bold uppercase tracking-wider mb-1">
                                  CLIENT-PROVIDED
                                </p>
                              </div>
                              <div></div>
                              <div></div>
                            </div>
                          )}
                          {childrenCom?.map((c: any, cIdx: number) => {
                            const isFeeRow =
                              c?.rowType === 'project-fee' || c?.rowType === 'order-fee';
                            const isServiceRow = c?.rowType === 'service';
                            const isFabricationRow = c?.rowType === 'fabrication';

                            return (
                              <div
                                key={cIdx}
                                className="grid md:grid-cols-[120px_1fr_60px_90px] grid-cols-[1fr_2fr_1fr_1fr] gap-3 py-2 px-2 items-start"
                              >
                                <div className="rounded">
                                  {c?.photoId ? (
                                    <div className="h-[56px] w-[56px]">
                                      <img
                                        alt={''}
                                        src={resolveImageSrc(c?.photoId)}
                                        className="h-full w-full rounded object-cover"
                                      />
                                    </div>
                                  ) : null}
                                </div>

                                <div>
                                  {(c?.qboType || '')?.toLowerCase()?.includes('hardware') && (
                                    <p className="m-0 text-xs text-gray-600 wrap-text whitespace-pre-wrap">
                                      HARDWARE
                                    </p>
                                  )}
                                  {(c?.qboType || '')?.toLowerCase()?.includes('headrail') && (
                                    <p className="m-0 text-xs text-gray-600 wrap-text whitespace-pre-wrap">
                                      HEADRAIL
                                    </p>
                                  )}
                                  {['parts', 'supplies'].some((s) =>
                                    (c?.qboType || '')?.toLowerCase()?.includes(s)
                                  ) && (
                                    <p className="m-0 text-xs text-gray-600 wrap-text whitespace-pre-wrap">
                                      {String(c?.displayTypeLabel || c?.qboItemName || '').trim() ||
                                        'Parts & Supplies'}
                                    </p>
                                  )}
                                  {isFeeRow && (
                                    <p className="m-0 text-xs text-sky-700 font-semibold wrap-text whitespace-pre-wrap">
                                      {c?.itemLabel || ''}
                                    </p>
                                  )}
                                  <p className="m-0 text-xs text-gray-600 wrap-text whitespace-pre-wrap">
                                    {c?.descriptionShort || c?.description || ''}
                                  </p>
                                </div>
                                <div className="text-right text-xs text-gray-600">
                                  {c?.quantity}{' '}
                                  {(c?.rowType === 'component' &&
                                    ['hardware', 'headrail', 'parts', 'supplies'].some((s) =>
                                      (c?.qboType || '')?.toLowerCase()?.includes(s)
                                    )) ||
                                  isFeeRow ||
                                  isServiceRow ||
                                  isFabricationRow
                                    ? ''
                                    : 'yd'}
                                </div>
                                <div className="text-right text-xs text-gray-500 italic">COM</div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Miscellaneous */}
        {projectFees?.length > 0 && (
          <div className="mb-2">
            {/* Miscellaneous header */}
            <div className="sticky top-0 grid md:grid-cols-[120px_1fr_60px_90px] grid-cols-[1fr_2fr_1fr_1fr] gap-3 border-b border-gray-200 py-2 px-2 bg-[#EAF3F6]">
              <span className="font-bold text-sm uppercase tracking-wider col-span-3">
                MISCELLANEOUS
              </span>
              <span className="font-bold text-sm text-right">
                {valuteNumberFormatter(projectFeesTotal)}
              </span>
            </div>
            {/* Miscellaneous body */}
            {projectFees?.map((item: any, index: number) => {
              return (
                <div
                  key={index}
                  className="grid md:grid-cols-[120px_1fr_60px_90px] grid-cols-[1fr_2fr_1fr_1fr] gap-3 py-2 px-2 border-b border-gray-100 items-start"
                >
                  <div></div>
                  <div>
                    <p className="m-0 wrap-text text-teal text-sm font-medium">{item?.itemLabel}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{item?.description}</p>
                  </div>
                  <div></div>
                  <div className="text-right text-sm">{valuteNumberFormatter(item?.amount)}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Estimate Total */}
        <div className="flex justify-end mb-4">
          <div className="rounded-md w-[280px] border border-teal/10">
            <div className="flex justify-between p-2 text-sm">
              <span>Subtotal</span>
              <span className="font-semibold">{valuteNumberFormatter(subTotal)}</span>
            </div>
            {discount && discountAmount !== 0 ? (
              <div className="flex justify-between p-2 text-sm text-gray-500">
                <span>{discountLabel}</span>
                <span>{valuteNumberFormatter(discountAmount, 'en-US', 'USD', true)}</span>
              </div>
            ) : null}
            <div className="flex justify-between p-2 text-sm text-gray-500">
              <span>{showTaxName()}</span>
              <span>{showTaxValue()}</span>
            </div>
            <div className="flex justify-between p-2 font-bold border-t border-teal">
              <span>Estimated Total</span>
              <span>
                {valuteNumberFormatter(estimatedGrandTotal)}
              </span>
            </div>
          </div>
        </div>
        {/* Payment Terms */}
        {data?.estimateBody?.paymentTerms && (
          <div className="mb-4">
            <h3 className="text-teal font-bold text-sm m-0 mb-1">Payment Terms</h3>
            <p className="m-0 text-sm text-gray-700 wrap-text whitespace-pre-wrap">
              {data?.estimateBody?.paymentTerms || ''}
            </p>
          </div>
        )}
        {/* Terms & Conditions */}
        {data?.estimateBody?.termsAndConditions && (
          <div className="mb-4">
            <h3 className="text-teal font-bold text-sm m-0 mb-1">Terms & Conditions</h3>
            <p className="m-0 text-sm text-gray-700 wrap-text whitespace-pre-wrap">
              {data?.estimateBody?.termsAndConditions}
            </p>
          </div>
        )}
        {/* Note */}
        <div className="mb-6">
          <h3 className="text-teal font-bold text-sm m-0 mb-1">Note</h3>
          <p className="m-0 text-sm text-gray-600 italic wrap-text whitespace-pre-wrap">
            {data?.estimateBody?.noteToCustomer || ''}
          </p>
        </div>
        {/* Footer */}
        <div className="text-center mt-10 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
            <span>Powered by</span>
            <img alt="QUIPA" src={QuipaLogo} className="h-5 object-contain" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EstimatePreview;
